// Stage title, geometry badge, loader, gesture line and the detailed readout
// in the Rendering section.
import type { App } from '../app.ts';
import { $ } from './dom.ts';
import { feasibleScale } from '../export/png.ts';
import { displayFor } from './curve-meta.ts';

export function mountReadout(app: App): void {
  const lblInfo = $('lblInfo');
  const shotHint = $('shotHint');
  const curveTitle = $('curveTitle');
  const curveSubtitle = $('curveSubtitle');
  const vertexCount = $('vertexCount');
  const geometryNote = $('geometryNote');
  const gestures = $('gestures');
  const loading = $('loading');
  const loadingText = $('loadingText');
  const canvas = app.renderer.canvas;

  const render = () => {
    const s = app.state;
    const exp = app.exp;
    const f = app.lastFrame;
    const d = displayFor(s.curve);
    const name = d?.name ?? app.curve.name;
    const is3d = app.is3d();

    curveTitle.textContent = name;
    curveSubtitle.textContent = (d?.subtitle ?? (is3d ? '3D' : '2D')) + ' · ' + exp.label;

    const stats = f?.stats;
    const shownStride = app.loop.refinedStride || stats?.stride || 1;
    const full = app.isFullDrawing();
    if (app.stage === 'loading' || !stats) {
      vertexCount.textContent = 'Loading…';
      geometryNote.textContent = 'PREPARING ' + exp.n_points.toLocaleString() + ' VERTICES';
    } else {
      vertexCount.textContent = exp.n_points.toLocaleString() + ' vertices';
      const what = app.stage === 'preview' ? 'PREVIEW · LOADING THE FULL DRAWING'
        : shownStride > 1 ? 'SAMPLED 1 IN ' + shownStride
        : full ? 'COMPLETE DRAWING' : 'SELECTED PORTION';
      geometryNote.textContent = what + ' · ' + (is3d ? '3D' : '2D');
    }
    loading.hidden = app.stage !== 'loading';
    loadingText.textContent = 'Preparing ' + name + '…';
    gestures.textContent = is3d
      ? 'Drag to orbit · Shift-drag to pan · Scroll to zoom · G for a clean canvas'
      : 'Drag to pan · Scroll to zoom · G for a clean canvas';
    canvas.setAttribute('aria-label', name + ' drawing. ' + (is3d ? 'Drag to orbit, shift-drag to pan.' : 'Drag to pan.') + ' Scroll or pinch to zoom.');

    const zoom = app.cam.scale > 0 ? app.cam.scale : 1;
    const lines: string[] = ['<b>' + name + '</b> · ' + exp.label];
    if (app.stage === 'loading' || !f || !stats) {
      lines.push('points <b>' + exp.n_points.toLocaleString() + '</b>', 'loading&hellip;');
    } else {
      const shownDrawn = app.loop.refinedStride
        ? Math.floor((stats.total - 1) / Math.max(1, shownStride)) + 1
        : stats.drawn;
      const every = shownStride === 1 && app.stage === 'full';
      lines.push(
        'points <b>' + exp.n_points.toLocaleString() + '</b>' +
          (app.stage === 'preview'
            ? ' (preview ' + stats.total.toLocaleString() + ', loading full&hellip;)'
            : shownStride > 1
              ? ' (drawing ' + shownDrawn.toLocaleString() + ', 1 in ' + shownStride + ')'
              : full ? ' (drawing all)' : ' (drawing ' + shownDrawn.toLocaleString() + ' of the selected portion)'),
        'detail <b>' + (every ? 'every point' : f.refining ? 'refining&hellip;' : 'sampled') + '</b>',
        'last frame <b>' + f.ms + ' ms</b>',
      );
    }
    lines.push((is3d ? '3D' : '2D') + ' · scale <b>' + (zoom < 0.01 ? zoom.toExponential(2) : zoom.toPrecision(4)) + '</b> px/unit');
    const gpu = (app.renderer as { gpuInfo?: string }).gpuInfo;
    lines.push('renderer <b>' + app.renderer.kind + '</b>' + (gpu ? ' · ' + gpu : ''));
    lblInfo.innerHTML = lines.join('<br/>');

    const want = s.shot.scale === 'max' ? Infinity : s.shot.scale;
    const used = Math.min(want, feasibleScale(Math.max(2, app.viewW), Math.max(2, app.viewH)));
    const W = Math.round(app.viewW * used), H = Math.round(app.viewH * used);
    shotHint.innerHTML = 'whole view → <b>' + W.toLocaleString() + ' × ' + H.toLocaleString() + '</b> px (' + ((W * H) / 1e6).toFixed(0) + ' MP)';
  };

  app.listeners.add(render);
  app.store.subscribe(render);
  render();
}
